import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { useForm } from 'react-hook-form'
import { mutualTheme } from '~/utils/theme'
import { LockIcon } from '~/assets/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { Checkbox, FormControlLabel, InputAdornment } from '@material-ui/core'
import { OnboardingFormLayout, HfPasswordInput } from '~/components'
import { CreateAccountFormValues, useCreateAccountFormStore } from '~/store/createAccountForm'
import * as yup from 'yup'
import * as cnpj from '@fnando/cnpj'
import { useApi } from '~/store/store'
import { toast } from 'react-toastify'
import { useSignIn } from '~/store/auth/useSignIn'
import { trackEvent, AuthActions, OnboardingWizardActions } from '~/service/analytics'
import { formatValuesToCreateAccount } from '~/utils/formatAccountValues'

const validationSchema = yup.object().shape({
  password: yup.string().required('Campo obrigatório')
    .test('Password length', 'Senha deve possuir 6 caracteres', password => password ? password.length === 6 : false)
    .matches(/^\d{6}$/, 'Senha deve conter apenas números'),
  passwordConfirmation: yup.string().required('Campo obrigatório')
    .oneOf([yup.ref('password'), null], 'Senhas devem coincidir')
    .required('Senha não pode ser nula')
})

export const RegisterData: React.FC = () => {
  const history = useHistory()
  const [visiblePassword, setVisiblePassword] = useState(false)
  const [createPjAccountWithPendencies, createPjAccountWithPendenciesState] = useApi('createPjAccountWithPendencies')
  const [sendCreateAccountLead, sendCreateAccountLeadState] = useApi('sendCreateAccountLead')
  const [createInitialAnalysis, createInitialAnalysisState] = useApi('createInitialAnalysis')

  const { createAccountFormState, updateForm } = useCreateAccountFormStore()
  const { form } = createAccountFormState
  const [signInState, signIn] = useSignIn()

  const { loading: createLeadLoading } = createPjAccountWithPendenciesState
  const { loading: sendCreateAccountLoading } = sendCreateAccountLeadState

  const isLoading = useMemo(() =>
    createLeadLoading || sendCreateAccountLoading || signInState.loading || createInitialAnalysisState.loading
  , [createLeadLoading, sendCreateAccountLoading, signInState.loading, createInitialAnalysisState.loading])

  const { handleSubmit, control, formState: { errors } } = useForm<CreateAccountFormValues>({
    defaultValues: form,
    resolver: yupResolver(validationSchema)
  })

  useEffect(() => {
    if (form.loanMotive === '') {
      history.push('/register/loan-info')
    }
  }, [form])

  const onSubmit = useCallback(async (data: CreateAccountFormValues) => {
    updateForm(data)
    const formatValuesToAccount = formatValuesToCreateAccount(data, form)

    const { data: account, error } = await createPjAccountWithPendencies(formatValuesToAccount)

    if (error || !account) {
      trackEvent(OnboardingWizardActions.SEND_SOLICITATION_ERROR)
      toast.error('Erro ao enviar solicitação, tente novamente', {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
        closeButton: false
      })
      trackEvent(AuthActions.CREATE_ACCOUNT_ERROR)
      return
    }

    trackEvent(OnboardingWizardActions.CREATED_ACCOUNT)

    const success = await signIn({
      password: data.password,
      username: cnpj.strip(form.cnpj)
    })

    if (success) {
      trackEvent(OnboardingWizardActions.SEND_SOLICITATION)

      await sendCreateAccountLead({
        accountId: account._id,
        email: account.email
      })

      trackEvent(AuthActions.CREATE_ACCOUNT)

      await createInitialAnalysis({ accountId: account._id })
      trackEvent(OnboardingWizardActions.INITIAL_ANALYSIS)

      history.push('/register/feedback')
      return
    }

    trackEvent(OnboardingWizardActions.SEND_SOLICITATION_ERROR)
    toast.error('Erro ao enviar solicitação, tente novamente', {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      closeButton: false
    })
  }, [updateForm, form, createPjAccountWithPendencies, signIn, sendCreateAccountLead, createInitialAnalysis])

  const onBackClick = useCallback(() => {
    history.goBack()
  }, [history])

  const changePasswordVisibility = useCallback(() => {
    setVisiblePassword(!visiblePassword)
  }, [visiblePassword])

  return (
    <OnboardingFormLayout
      title='Crie uma senha de acesso'
      subtitle='Você está quase lá! Crie uma conta para acompanhar sua solicitação.'
      isLastPage
      isLoading={isLoading}
      canContinue
      onNextClick={handleSubmit(onSubmit)}
      onBackClick={onBackClick}
    >
      <HfPasswordInput
        isVisible={visiblePassword}
        name='password'
        control={control}
        label='Senha'
        error={errors.password?.message}
        errorMessage={errors.password?.message}
        placeholder='Crie uma senha de 6 números'
        endAdornment={<InputAdornment position='end'><LockIcon htmlColor={mutualTheme.palette.textGray4.main} /></InputAdornment>}
      />

      <HfPasswordInput
        isVisible={visiblePassword}
        control={control}
        label='Repetir senha'
        name='passwordConfirmation'
        placeholder='Repita a senha'
        error={errors.passwordConfirmation?.message}
        errorMessage={errors.passwordConfirmation?.message}
        endAdornment={<InputAdornment position='end'><LockIcon htmlColor={mutualTheme.palette.textGray4.main} /></InputAdornment>}
      />

      <FormControlLabel
        label='Mostrar senha'
        sx={{
          color: mutualTheme.palette.textGray1.main,
          fontFamily: 'Airbnb'
        }}
        control={<Checkbox name='passwordVisibility' checked={visiblePassword} onChange={changePasswordVisibility} />}
      />
    </OnboardingFormLayout>
  )
}
